import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useApp } from '../context/usePortal';
import { personalInfoOf } from '../utils/security-phrase';
import { SecuritySetupForm } from './SecuritySetupForm';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from './ui/dialog';

const SEEN_KEY_PREFIX = 'security_setup_prompt_seen:';

const wasSeen = (userId: string): boolean => {
  try {
    return sessionStorage.getItem(SEEN_KEY_PREFIX + userId) === '1';
  } catch {
    return false;
  }
};

const markSeen = (userId: string) => {
  try {
    sessionStorage.setItem(SEEN_KEY_PREFIX + userId, '1');
  } catch {
    // Storage blocked: the prompt may show again next visit, which is harmless.
  }
};

const descriptionFor = (needsAvatar: boolean, needsPhrase: boolean) => {
  if (needsAvatar && needsPhrase)
    return 'Two quick steps, so you always know a payment page is really AyeFace.';
  if (needsAvatar)
    return 'One quick step, so you always know a payment page is really AyeFace.';
  return "Choose a security phrase. We'll show it at every checkout, so you know the page is really AyeFace.";
};

/**
 * Asks a signed-in user who has no security avatar or no security phrase yet
 * to set what is missing, right here. Shown once per browser session, never
 * on the Settings page itself, and gone for good once both are saved.
 */
const SecuritySetupPrompt: React.FC = () => {
  const { user, refreshUser } = useApp();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  // What was missing when the prompt opened; it keeps its fields while open.
  const [asks, setAsks] = useState({ avatar: false, phrase: false });
  const inputRef = useRef<HTMLInputElement>(null);

  const userId = user?.user_id;
  const needsAvatar = !!user && !user.has_security_avatar;
  const needsPhrase = !!user && !user.security_phrase;
  const onSettings = pathname.startsWith('/settings');

  useEffect(() => {
    if (
      userId &&
      (needsAvatar || needsPhrase) &&
      !onSettings &&
      !open &&
      !wasSeen(userId)
    ) {
      setAsks({ avatar: needsAvatar, phrase: needsPhrase });
      setOpen(true);
    }
  }, [userId, needsAvatar, needsPhrase, onSettings, open]);

  const close = () => {
    if (userId) markSeen(userId);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent
        className="max-h-[calc(100dvh-2rem)] w-[calc(100%-2rem)] max-w-[25rem] overflow-y-auto rounded-3xl focus:outline-none sm:max-w-[26rem]"
        onOpenAutoFocus={(event) => {
          // Start in the phrase field rather than on the "Suggest one" link.
          event.preventDefault();
          if (!asks.avatar) inputRef.current?.focus();
        }}
      >
        <div className="flex flex-col pb-1 pt-3">
          <DialogHeader className="items-center">
            <span className="rounded-full bg-[#E3F2EA] px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#2E6B4F]">
              New safeguard
            </span>
            <DialogTitle className="mt-3 text-center text-xl font-bold text-gray-900">
              Protect your payments
            </DialogTitle>
            <DialogDescription className="mt-2 text-center text-sm leading-6 text-gray-600">
              {descriptionFor(asks.avatar, asks.phrase)}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-5">
            <SecuritySetupForm
              showAvatar={asks.avatar}
              showPhrase={asks.phrase}
              hasAvatar={!asks.avatar}
              hasPhrase={!asks.phrase}
              personal={personalInfoOf(user)}
              phraseInputId="prompt-security-phrase"
              phraseInputRef={inputRef}
              submitLabel="Save"
              phrasePreview={(preview) => (
                <div
                  className="mt-4 overflow-hidden rounded-xl border border-gray-200"
                  aria-label="What you'll see at checkout"
                >
                  <p className="bg-gray-50 px-3 py-2 text-xs text-gray-600">
                    What you&apos;ll see at checkout
                  </p>
                  <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 px-3 py-3">
                    <span className="text-xs font-semibold text-gray-900">
                      AyeFace Checkout
                    </span>
                    <span
                      className="whitespace-nowrap rounded-full bg-[#E3F2EA] px-3 py-1 text-xs font-medium text-[#2E6B4F]"
                      data-testid="security-phrase-preview"
                    >
                      Your phrase: {preview}
                    </span>
                  </div>
                </div>
              )}
              onSaved={() => {
                close();
                refreshUser();
              }}
            />
          </div>
          <button
            type="button"
            onClick={close}
            className="mt-3 self-center rounded-full px-6 py-2 text-sm text-gray-800 hover:text-gray-950"
          >
            Later
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SecuritySetupPrompt;
